/** The participant chrome around a page: top bar, rail, the offline banner and the page itself (screens §1.3, §3.1). */
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RouteCall } from "../api/types";
import type { PageDeps } from "../router";
import { useRoute } from "../api/context";
import { useSignOut } from "../auth";
import { postCheckSent, PRE_CHECK_LATER_LINE, preCheckLater } from "../checkin/postDone";
import { limitsOf } from "../decisions/limits";
import { SurpriseTakeover } from "../events/EventsTab";
import { clockTime, fiscalYearLabel } from "../format";
import { GlossaryProvider } from "../theme/GxTerm";
import { GxNotice } from "../theme/primitives";
import { chairOwed } from "./chair";
import { clearCalledIn, useCalledIn } from "./calledIn";
import { useReopenedRound } from "./reopened";
import { chairStepOf, roundGuide, viewedStepOf } from "./guide";
import { outlookRoundOf, useOutlookSeen } from "../outlook/seen";
import { Link, useLocation } from "./history";
import { preCheckPath } from "../checkin/handoff";
import { useScrollToHash } from "./useScrollToHash";
import { GxRail, OutlookDot, railItems, type OutlookDotInfo } from "./Rail";
import { GxNavSheet } from "./NavSheet";
import { isMobileBand, useBand } from "../theme/breakpoints";
import { GxGuideStrip } from "./RoundGuide";
import { landingPath } from "./landing";
import { useAccountItems } from "./runs";
import { NotFound } from "./NotFound";
import { rememberBank, rememberRound } from "./lastKnown";
import { GxRoundClock } from "./RoundClock";
import { outstandingSteps } from "../decisions/nextStep";
import { DeskLabelProvider } from "./desks";
import { GxTopBar } from "./TopBar";
import { ActingAsChip, useWorkingRole } from "./actingAs";
import type { PreviewWorkerClient } from "../preview-worker";
import { PreviewProvider } from "./PreviewProvider";
import type { StreamHealth } from "../api/context";
import "./shell.css";

export function Frame({ cohortId, page, deps, health, lastSyncAt, createWorker, children }: {
  /** The preview worker factory (tests pass a fake). */
  readonly createWorker?: () => PreviewWorkerClient;
  readonly cohortId: string; readonly page: string; readonly deps: PageDeps; readonly health: StreamHealth; readonly lastSyncAt: Date | null; readonly children: ReactNode;
}) {
  // A surprise card's answer changes the round's starting point as a planned card's does: the preview context is read again, so the preview says "Updating" until the run on it lands.
  useScrollToHash();
  const queryClient = useQueryClient();
  const answerRoute = deps.api.route("respondToEvent");
  const respondToEvent = useMemo<RouteCall<"respondToEvent">>(() => async (params, body) => {
    const result = await answerRoute(params, body);
    if (result.ok) void queryClient.invalidateQueries({ queryKey: ["getPreviewContext"] });
    return result;
  }, [answerRoute, queryClient]);
  const team = useRoute("teamState", deps.teamId === null ? null : { teamId: deps.teamId });
  const catalog = useRoute("contentCatalog", { cohortId }, { staleTime: Infinity });
  const welcome = useRoute("welcomeCard", { cohortId }, { staleTime: Infinity });
  const signOut = useSignOut();
  const { pathname, search } = useLocation();
  const state = team.data;
  const round = state?.round ?? null;
  const released = state?.releasedRounds ?? [];
  // The outlook is drawn after the last released round, so the dot and the guide ask about the round after it (none once every round is released).
  const outlookRound = state === undefined || page === "chair" || round === null ? null : outlookRoundOf(released.at(-1)?.roundNo ?? 0);
  const outlookRoundOpen = outlookRound !== null && state !== undefined && outlookRound <= state.cohort.roundCount ? outlookRound : null;
  const outlookOpened = useOutlookSeen(cohortId, outlookRoundOpen);
  // The clock speaks of the open round; on a page about another year (an earlier year's results) it says which year it is looking at instead of the latest round's state.
  const viewed = page === "results" ? Number(pathname.split("/")[4]) : NaN;
  const clockLabel = round !== null && catalog.data !== undefined && Number.isInteger(viewed) && viewed !== round.roundNo ? `${fiscalYearLabel(catalog.data, viewed)} results` : undefined;
  // The dot asks about the year the outlook is drawn for, which on a results view is the NEXT year while the round steps describe this one: it says which year.
  const outlookDot: OutlookDotInfo | null = outlookRoundOpen === null || outlookOpened ? null
    : { label: catalog.data === undefined ? "Next year's outlook is not opened yet" : `The ${fiscalYearLabel(catalog.data, outlookRoundOpen)} outlook is not opened yet` };
  const reopenedRound = useReopenedRound(cohortId);
  const calledInNow = useCalledIn(cohortId);
  // Called in this session (the stream) or on the server record (it survives a reload and another device).
  // "Do it later" answers the call: the banner stays quiet (the waiting line takes over) until the facilitator calls again.
  const calledIn = (calledInNow || state?.me.calledToPreCheck === true) && !preCheckLater(cohortId);
  // The facilitator released the post-check: every page says so until this person has sent it (the stream refreshes the team state, so it appears at once).
  const postCheckOpen = state !== undefined && state.cohort.mode !== "solo" && state.cohort.postAssessmentOpen && !postCheckSent(cohortId);
  // A person who put the pre-check off keeps being told until they take it or the post-check opens (it is remembered in this browser).
  const waitingPre = state !== undefined && state.cohort.mode !== "solo" && !state.cohort.postAssessmentOpen && preCheckLater(cohortId);
  const workingRole = useWorkingRole(state?.me.role ?? null, deps.principal.userId, state?.me.actingFor);
  // Once the plan is submitted there is nothing left to do on Decide: a card that stops the year is answered on the run page, not counted there.
  const decidingNow = round === null || round.soloPhase === null || round.soloPhase === "deciding";
  const outstanding = state === undefined ? 0 : outstandingSteps(state);
  // At 768 px and below the menu sheet takes the rail's place (shell.css hides the rail there too; leaving it out of the page keeps one list of steps for a reader).
  const mobile = isMobileBand(useBand());
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const owedChair = state !== undefined && state.cohort.mode !== "solo" && chairOwed(state);
  const latestReleased = released.at(-1)?.roundNo ?? null;
  const items = railItems({ cohortId, active: page, latestReleasedRound: latestReleased, chairOwed: owedChair });
  const guide = state === undefined ? null : roundGuide(state, cohortId, page === "chair" ? chairStepOf(search) : null, outlookOpened, round === null ? null : viewedStepOf(pathname, search, round.roundNo));
  const bankName = rememberBank(welcome.data?.bankName) ?? "Governexus";
  const subline = state === undefined ? "" : state.cohort.mode === "solo" ? "Solo run" : state.team.name;
  const accountItems = useAccountItems();
  const acting = <ActingAsChip ownRole={state?.me.role ?? null} userId={deps.principal.userId} server={state?.me.actingFor} />;
  // The phone bar names the page: the rail row for it, or "Take the chair" while the player is on the chair (its row leaves the rail then).
  const title = page === "chair" ? "Take the chair" : items.find((i) => i.key === page)?.label ?? bankName;
  // An address for a program that is not there (or not this person's): said as not found, with a way back, not a rail of live links and a retry that cannot help.
  if (catalog.error?.result.status === 404) return <NotFound backTo={landingPath(deps.principal)} title="We couldn't find that program." />;
  return (
    <GlossaryProvider value={catalog.data?.glossary ?? []}>
      <DeskLabelProvider catalog={catalog.data}>
      <PreviewProvider teamId={deps.teamId} roundId={round?.id ?? null} createWorker={createWorker}>
      <GxTopBar
        bankName={bankName}
        subline={subline}
        role={workingRole}
        acting={acting}
        name={deps.principal.displayName}
        email={deps.principal.email}
        onSignOut={signOut}
        accountItems={accountItems}
        title={title} onMenu={() => setMenuOpen(true)} menuOpen={menuOpen}
        clock={round === null ? null : <GxRoundClock round={round} warningMinutes={state?.cohort.countdownWarningMinutes ?? 5} catalog={catalog.data ?? null} solo={state?.cohort.mode === "solo"} {...(clockLabel === undefined ? {} : { label: clockLabel })} />}
      />
      {guide === null || !mobile ? null : <GxGuideStrip guide={guide} />}
      <div className="gx-shell">
        {mobile ? null : (
          <GxRail
            cohortId={cohortId} active={page}
            heading={rememberRound(page === "chair" ? "Take the chair" : round === null || catalog.data === undefined ? null : `Round ${round.roundNo} · ${fiscalYearLabel(catalog.data, round.roundNo)}`) ?? "Loading your round…"}
            decideBadge={page === "chair" || !decidingNow ? 0 : outstanding}
            decideBadgeLabel={`${outstanding} ${outstanding === 1 ? "step" : "steps"} to ${state?.cohort.mode === "solo" ? "submit" : "sign"}`}
            latestReleasedRound={released.at(-1)?.roundNo ?? null}
            outlookDot={outlookDot}
            chairOwed={owedChair}
            guide={guide}
          />
        )}
        <main className="gx-main">
          {health === "polling" ? <div style={{ marginBottom: 12 }}><GxNotice>Live updates paused. Reconnecting…{lastSyncAt === null ? "" : ` Last synced ${clockTime(lastSyncAt)}.`}</GxNotice></div> : null}
          {postCheckOpen ? <div style={{ marginBottom: 12 }}><GxNotice><span data-testid="shell.postCheck">Your facilitator has opened the post-check. <Link href={`/c/${cohortId}/check-in/post`} data-testid="shell.postCheck.link">Take the post-check</Link> when you are ready.</span></GxNotice></div> : null}
          {waitingPre ? <div style={{ marginBottom: 12 }}><GxNotice><span data-testid="shell.preLater">{PRE_CHECK_LATER_LINE}</span></GxNotice></div> : null}
          {calledIn ? <div style={{ marginBottom: 12 }}><GxNotice><span data-testid="shell.calledIn">Your facilitator has called you to the pre-check. <Link href={preCheckPath(cohortId, "in_room")} data-testid="shell.calledIn.link" onClick={() => clearCalledIn(cohortId)}>Take the pre-check</Link> now.</span></GxNotice></div> : null}
          {reopenedRound !== null && round?.state === "open" && round.roundNo === reopenedRound ? <div style={{ marginBottom: 12 }}><GxNotice><span data-testid="shell.reopened">Your facilitator reopened round {reopenedRound}, so you can change your plan again. Sign it again when you are ready.</span></GxNotice></div> : null}
          {children}
        </main>
        {state === undefined ? null : <SurpriseTakeover team={state} respondToEvent={respondToEvent} limits={catalog.data === undefined ? undefined : limitsOf(state, catalog.data)} />}
      </div>
      {menuOpen ? (
        <GxNavSheet onClose={closeMenu} bankName={bankName} subline={subline} role={workingRole} acting={acting} items={items} active={page}
          badges={{ outlook: page !== "outlook" && outlookDot !== null ? <OutlookDot dot={outlookDot} /> : null }}
          coach={{ active: page }} guide={guide} name={deps.principal.displayName} email={deps.principal.email} accountItems={accountItems} onSignOut={signOut} />
      ) : null}
      </PreviewProvider>
      </DeskLabelProvider>
    </GlossaryProvider>
  );
}
